/**
 * Table — data grid component with Table + Column composition.
 *
 * Design system mapping (mch-table):
 *   • th: text-xs font-medium text-webex-gray-700 dark: webex-gray-300 px-4 py-3 (Webex th: 12/500, no uppercase)
 *   • td: text-sm text-webex-gray-1000 dark: white px-4 py-2.5
 *   • Border: border-b webex-gray-200 dark: webex-gray-800
 *   • Row hover: bg webex-gray-50 dark: webex-gray-900
 *   • Mobile (<md): renders as cards with field labels
 */
import {
  Children,
  Fragment,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react"
import { cn } from "@lib/cn"
import { Spinner } from "./Spinner"
import { Popover } from "./Popover"
import { Select } from "./Select"
import { Checkbox } from "./Checkbox"

function getNestedValueFromString(obj: Record<string, unknown>, key: string): unknown {
  return key.split(".").reduce<unknown>((acc, k) => {
    if (acc && typeof acc === "object" && k in (acc as object)) {
      return (acc as Record<string, unknown>)[k]
    }
    return undefined
  }, obj)
}

function useScrollDirection(buffer = 0) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [state, setState] = useState({ hasLeftScroll: false, hasRightScroll: false })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const check = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el
      setState({
        hasLeftScroll: scrollLeft > buffer,
        hasRightScroll: scrollWidth > scrollLeft + clientWidth + buffer,
      })
    }
    const ro = new ResizeObserver(check)
    ro.observe(el)
    check()
    el.addEventListener("scroll", check)
    return () => {
      el.removeEventListener("scroll", check)
      ro.unobserve(el)
    }
  }, [buffer])

  return { ref: containerRef, hasLeftScroll: state.hasLeftScroll, hasRightScroll: state.hasRightScroll }
}

interface TableHeader {
  title?: string
  field?: string
  sortField?: string
  sortable: boolean
  sortOrder: string | null
}

interface TableContextValue {
  empty: boolean
  loading: boolean
  columns: ReactNode[]
}

interface RowData {
  id?: string | number
  table_key?: string
  [key: string]: unknown
}

const TableContext = createContext<TableContextValue>({ empty: false, loading: false, columns: [] })
const RowContext = createContext<RowData>({})

interface TableProps {
  data?: RowData[]
  children: ReactNode
  loading?: boolean
  sortBy?: string
  sortOrder?: string
  onSort?: (sort: { by: string; order: string }) => void
  emptyMessage?: string
  stickyHeader?: boolean
  className?: string
  sortable?: boolean
  rowClassName?: string
  headerClassName?: string
  showColumnToggle?: boolean
}

export function Table({
  data,
  children,
  loading = false,
  sortBy: initialSortBy,
  sortOrder: initialSortOrder,
  onSort,
  emptyMessage,
  stickyHeader = false,
  className = "",
  sortable = true,
  rowClassName = "",
  headerClassName = "",
  showColumnToggle = false,
}: TableProps) {
  const [sortBy, setSortBy] = useState(initialSortBy || "id")
  const [sortOrder, setSortOrder] = useState(initialSortOrder || "asc")

  const columns = useMemo(
    () => Children.toArray(children).filter((column) => column !== ""),
    [children],
  )

  const sortedData = useMemo(() => {
    if (!data || !sortBy) return data
    return [...data].sort((a, b) => {
      const aValue = getNestedValueFromString(a as Record<string, unknown>, sortBy)
      const bValue = getNestedValueFromString(b as Record<string, unknown>, sortBy)

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return sortOrder === "asc" ? 1 : -1
      if (bValue == null) return sortOrder === "asc" ? -1 : 1
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
      }
      if ((aValue as number) < (bValue as number)) return sortOrder === "asc" ? -1 : 1
      if ((aValue as number) > (bValue as number)) return sortOrder === "asc" ? 1 : -1
      return 0
    })
  }, [data, sortBy, sortOrder])

  const headers = useMemo<TableHeader[]>(
    () =>
      columns.map((column) => {
        const col = column as ReactElement<ColumnProps>
        if (!col.props) return { sortable: false, sortOrder: null }
        return {
          title: col.props.title,
          field: col.props.field,
          sortField: col.props.sortField,
          sortable: col.props.sortable === undefined ? sortable : col.props.sortable,
          sortOrder: sortBy === col.props.field ? sortOrder : null,
        }
      }),
    [columns, sortBy, sortOrder, sortable],
  )

  const sortByHeader = useMemo(
    () => headers.find((h) => h.field === sortBy),
    [headers, sortBy],
  )
  const empty = useMemo(() => !data || data.length === 0, [data])

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])
  const [manualHeight, setManualHeight] = useState<number | null>(null)
  const { ref: scrollRef, hasLeftScroll, hasRightScroll } = useScrollDirection(20)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!stickyHeader) return
    const update = () => {
      if (!containerRef.current) return
      const vh = window.innerHeight
      let h = vh - containerRef.current.getBoundingClientRect().top - 100 - window.scrollY
      if (h < 60) h = 60
      setManualHeight(h)
    }
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [stickyHeader])

  const handleSort = (header: TableHeader) => {
    if (!header.sortable) return
    const newSortBy = header.sortField || header.field || ""
    setSortBy(newSortBy)
    const newSortOrder = sortBy === newSortBy && sortOrder === "asc" ? "desc" : "asc"
    setSortOrder(newSortOrder)
    onSort?.({ by: newSortBy, order: newSortOrder })
  }

  const stickyHeaderCss = stickyHeader
    ? "sticky top-0 z-[2] bg-webex-gray-50 dark:bg-webex-gray-900"
    : ""

  const columnTogglePopover = showColumnToggle ? (
    <Popover.Root>
      <Popover.Trigger
        render={
          <button
            type="button"
            aria-label="Toggle columns"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 rounded p-1 text-webex-gray-600 dark:text-webex-gray-400 hover:bg-webex-gray-100 dark:hover:bg-webex-gray-850 hover:text-webex-gray-1000 dark:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600"
          >
            <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden>
              <circle cx="8" cy="3" r="1.25" />
              <circle cx="8" cy="8" r="1.25" />
              <circle cx="8" cy="13" r="1.25" />
            </svg>
          </button>
        }
      />
      <Popover.Content side="bottom" align="end" className="w-56 p-0">
        <div className="px-3 py-2 border-b border-webex-gray-200 dark:border-webex-gray-800">
          <h3 className="text-sm font-medium text-webex-gray-1000 dark:text-white">
            Toggle Columns
          </h3>
        </div>
        <div className="flex flex-col gap-1 overflow-y-auto max-h-48 px-3 py-2">
          {columns
            .filter((column) => {
              const col = column as ReactElement<ColumnProps>
              return col.props.hideable !== false && col.props.title
            })
            .map((column, idx) => {
              const col = column as ReactElement<ColumnProps>
              const title = col.props.title || ""
              const visible = !hiddenColumns.includes(title)
              return (
                <label key={title || idx} className="flex items-center gap-2 py-1 cursor-pointer">
                  <Checkbox
                    checked={visible}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setHiddenColumns((prev) => prev.filter((c) => c !== title))
                      } else {
                        setHiddenColumns((prev) => [...prev, title])
                      }
                    }}
                  />
                  <span className="text-sm text-webex-gray-1000 dark:text-white">{title}</span>
                </label>
              )
            })}
        </div>
      </Popover.Content>
    </Popover.Root>
  ) : null

  return (
    <TableContext.Provider value={{ empty, loading, columns }}>
      <div className="relative" ref={containerRef}>
        {/* Mobile sort controls */}
        <div className="md:hidden flex gap-3 items-center mx-4 mb-3">
          <span className="text-sm text-webex-gray-700 dark:text-webex-gray-300">Sort:</span>
          <Select
            size="sm"
            aria-label="Sort by"
            value={sortByHeader?.field ?? ""}
            onChange={(e) => {
              const newField = e.target.value
              const found = headers.find((h) => h.field === newField)
              if (found) handleSort(found)
            }}
            options={headers
              .filter((h) => h.sortable && h.title)
              .map((h) => ({ value: h.field || "", label: h.title || "" }))}
          />
          <Select
            size="sm"
            aria-label="Sort direction"
            value={sortOrder}
            onChange={(e) => {
              const newOrder = e.target.value
              setSortOrder(newOrder)
              onSort?.({ by: sortBy, order: newOrder })
            }}
            options={[
              { value: "asc", label: "Asc" },
              { value: "desc", label: "Desc" },
            ]}
          />
        </div>

        {loading && (
          <div
            role="status"
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-webex-gray-950/80"
          >
            <Spinner size="3xl" ariaLabel="Loading…" />
          </div>
        )}

        <div
          className={cn(
            "overflow-x-auto z-0",
            manualHeight ? "overflow-y-scroll" : "overflow-y-hidden",
          )}
          style={manualHeight ? { maxHeight: `${manualHeight}px` } : undefined}
          ref={scrollRef}
        >
          {/* Edge scroll shadows */}
          <div
            className={cn(
              "pointer-events-none absolute top-0 left-0 h-[calc(100%-1px)] w-24 z-10 transition-opacity duration-300",
              "bg-gradient-to-r from-webex-gray-50/80 dark:from-webex-gray-950/80 from-15% to-transparent",
              hasLeftScroll ? "opacity-100" : "opacity-0",
            )}
          />
          <div
            className={cn(
              "pointer-events-none absolute top-0 right-0 h-[calc(100%-1px)] w-24 z-10 transition-opacity duration-300",
              "bg-gradient-to-l from-webex-gray-50/80 dark:from-webex-gray-950/80 from-15% to-transparent",
              hasRightScroll ? "opacity-100" : "opacity-0",
            )}
          />

          <table className={cn("min-w-full text-sm", className)}>
            <thead className="hidden md:table-header-group bg-webex-gray-100 dark:bg-webex-gray-1000">
              <tr
                className={cn(
                  "border-b border-webex-gray-200 dark:border-webex-gray-800",
                  headerClassName,
                )}
              >
                {headers.map((header, idx) => {
                  if (hiddenColumns.includes(header.title || "")) return null
                  const visibleHeaders = headers.filter((h) => !hiddenColumns.includes(h.title || ""))
                  const isLast = visibleHeaders[visibleHeaders.length - 1] === header
                  return (
                    <th
                      key={header.title || idx}
                      scope="col"
                      aria-sort={
                        header.sortOrder === "asc"
                          ? "ascending"
                          : header.sortOrder === "desc"
                            ? "descending"
                            : "none"
                      }
                      className={cn(
                        stickyHeaderCss,
                        "px-4 py-3 text-left text-xs font-medium whitespace-nowrap",
                        "text-webex-gray-700 dark:text-webex-gray-300",
                        isLast && showColumnToggle && "relative pr-10",
                        header.sortable &&
                          "cursor-pointer hover:bg-webex-gray-100 dark:hover:bg-webex-gray-850 select-none",
                      )}
                      onClick={() => handleSort(header)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {header.title}
                        {header.sortOrder && (
                          <svg
                            aria-hidden
                            viewBox="0 0 16 16"
                            className="size-3 text-webex-gray-500 dark:text-webex-gray-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.25"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            {header.sortOrder === "asc" ? (
                              <path d="M8 3v10M4 7l4-4 4 4" />
                            ) : (
                              <path d="M8 3v10M4 9l4 4 4-4" />
                            )}
                          </svg>
                        )}
                      </span>
                      {isLast && columnTogglePopover}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="block md:table-row-group bg-white dark:bg-webex-gray-950">
              {sortedData?.map((row, rowIdx) => (
                <RowContext.Provider value={row} key={row.table_key || row.id || rowIdx}>
                  <tr
                    className={cn(
                      "block md:table-row border-b border-webex-gray-200 dark:border-webex-gray-800",
                      "hover:bg-webex-gray-100 dark:hover:bg-webex-gray-850",
                      rowClassName,
                    )}
                  >
                    {columns.map((column, colIdx) => {
                      const col = column as ReactElement<ColumnProps>
                      return (
                        !hiddenColumns.includes(col.props.title || "") && (
                          <td
                            key={col.props.title || colIdx}
                            style={col.props.style}
                            className={cn(
                              "block md:table-cell px-4 py-2.5 text-sm text-webex-gray-1000 dark:text-white whitespace-nowrap",
                              col.props.className,
                            )}
                          >
                            {headers[colIdx]?.title && (
                              <span className="md:hidden mr-2 font-medium text-webex-gray-600 dark:text-webex-gray-400">
                                {headers[colIdx].title}:
                              </span>
                            )}
                            {column}
                          </td>
                        )
                      )
                    })}
                  </tr>
                </RowContext.Provider>
              ))}

              {empty && !loading && <TableEmpty message={emptyMessage} />}
              {empty && loading && <TableEmpty message="Loading…" />}
            </tbody>
          </table>
        </div>
      </div>
    </TableContext.Provider>
  )
}

interface TableEmptyProps {
  message?: string
}

function TableEmpty({ message = "No data" }: TableEmptyProps) {
  const { empty, columns } = useContext(TableContext)
  if (!empty) return <Fragment />
  return (
    <tr>
      <td colSpan={columns.length}>
        <p className="px-6 py-8 text-sm text-webex-gray-600 dark:text-webex-gray-400">{message}</p>
      </td>
    </tr>
  )
}

interface ColumnProps {
  children?: ReactNode | ((row: RowData) => ReactNode)
  title?: string
  field?: string
  sortField?: string
  blankValue?: string
  sortable?: boolean
  hideable?: boolean
  className?: string
  style?: CSSProperties
}

export function Column({
  children = null,
  field,
  blankValue = "",
}: ColumnProps) {
  const row = useContext(RowContext)
  const hasChildren = Children.count(children) > 0
  const isFunction = typeof children === "function"

  if (isFunction) return <>{children(row)}</>
  if (hasChildren) return <>{children}</>
  if (!field) return <>{blankValue}</>

  let nestedValue = getNestedValueFromString(row as Record<string, unknown>, field)
  if (nestedValue && typeof nestedValue === "object") {
    nestedValue = JSON.stringify(nestedValue, null, 2)
  }
  if (typeof nestedValue === "boolean") {
    nestedValue = nestedValue ? "Yes" : "No"
  }
  return (
    <>
      {nestedValue != null && nestedValue !== "" ? String(nestedValue) : blankValue || ""}
    </>
  )
}
